import moment from "moment";

/**
 * Shared "add to calendar" helpers.
 *
 * Both builders take the same event shape:
 *
 * {
 *     uid: string|number,            // stable id used for the iCal UID
 *     title: string,
 *     start: moment|Date|string,     // anything moment() accepts
 *     end: moment|Date|string,       // optional - omitted means no end time
 *     location: string,
 *     descriptionLines: string[],
 *     timezone: string,              // IANA name, Google only
 *     filename: string,              // .ics download name, optional
 * }
 */

const STAMP_FORMAT = "YYYYMMDDTHHmmss";

/**
 * Values are encoded one at a time. encodeURI() leaves "&", "+" and "#"
 * untouched, so a single venue or team name containing one of them silently
 * truncates the whole calendar URL.
 */
function buildQuery(params) {
    return Object.keys(params)
        .filter(key => params[key] !== null && params[key] !== undefined && params[key] !== "")
        .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(params[key]))
        .join("&");
}

/**
 * Accepts a moment, a Date, a parseable string, or a value that is already in
 * the target "20260802T194500" shape (the draw endpoint pre-formats its dates
 * server side) - those are passed straight through rather than reparsed.
 */
function stamp(value) {
    if (!value) {
        return "";
    }

    if (typeof value === "string" && /^\d{8}T\d{6}$/.test(value)) {
        return value;
    }

    return moment(value).format(STAMP_FORMAT);
}

/**
 * Escape a value for an iCalendar TEXT property (RFC 5545 3.3.11).
 */
function escapeIcsText(value) {
    return String(value === null || value === undefined ? "" : value)
        .replace(/\\/g, "\\\\")
        .replace(/;/g, "\\;")
        .replace(/,/g, "\\,")
        .replace(/\r?\n/g, "\\n");
}

function lines(event) {
    return (event.descriptionLines || []).filter(line => line !== null && line !== undefined && line !== "");
}

/**
 * Compose a calendar LOCATION from the parts of a venue.
 *
 * Calendar clients geocode this string to drop a map pin and offer directions,
 * and a bare venue name often fails to resolve - so include the address
 * wherever the page has it. Parts the page doesn't carry are skipped.
 *
 * @param {{name?: string, address?: string, suburb?: string, state?: string, postcode?: string}} venue
 * @returns {string}
 */
export function formatLocation(venue) {
    const clean = value => (value === null || value === undefined ? "" : String(value).trim());
    const region = [venue.suburb, venue.state, venue.postcode].map(clean).filter(Boolean).join(" ");

    return [clean(venue.name), clean(venue.address), region].filter(Boolean).join(", ");
}

/**
 * UTF-8 byte length of a single character - the 75 octet limit below counts
 * bytes, not JavaScript characters.
 */
function octetLength(character) {
    const code = character.codePointAt(0);

    if (code < 0x80) {
        return 1;
    }
    if (code < 0x800) {
        return 2;
    }
    if (code < 0x10000) {
        return 3;
    }

    return 4;
}

/**
 * Fold a content line to 75 octets (RFC 5545 3.1). A long DESCRIPTION is
 * otherwise truncated by strict importers. Continuation lines start with a
 * single space, which the parser strips again when it unfolds them. Iterating
 * with for..of walks whole code points, so surrogate pairs are never split.
 */
function foldLine(line) {
    const LIMIT = 75;
    let folded = "";
    let used = 0;

    for (const character of line) {
        const size = octetLength(character);

        if (used + size > LIMIT) {
            folded += "\r\n ";
            used = 1;
        }

        folded += character;
        used += size;
    }

    return folded;
}

/**
 * Build a "add to Google Calendar" link.
 *
 * @param {object} event
 * @returns {string}
 */
export function googleCalendarUrl(event) {
    const start = stamp(event.start);
    const end = stamp(event.end) || start;

    const query = buildQuery({
        action: "TEMPLATE",
        ctz: event.timezone,
        dates: start + "/" + end,
        text: event.title,
        location: event.location,
        details: lines(event).join("<br>"),
    });

    return "https://calendar.google.com/calendar/render?" + query;
}

/**
 * Build the raw .ics payload. Times are deliberately left floating (no TZID,
 * no trailing Z) so they land at the local clock time of whoever imports them.
 *
 * @param {object} event
 * @returns {string}
 */
export function buildIcs(event) {
    const start = stamp(event.start);
    const end = stamp(event.end);

    const content = [
        "BEGIN:VCALENDAR",
        "VERSION:2.0",
        "PRODID:-//LMP//Roster//EN",
        "CALSCALE:GREGORIAN",
        "METHOD:PUBLISH",
        "BEGIN:VEVENT",
        "UID:" + escapeIcsText(event.uid || start) + "@" + window.location.hostname,
        "DTSTAMP:" + moment.utc().format(STAMP_FORMAT) + "Z",
        "DTSTART:" + start,
        end ? "DTEND:" + end : null,
        "SUMMARY:" + escapeIcsText(event.title),
        event.location ? "LOCATION:" + escapeIcsText(event.location) : null,
        "DESCRIPTION:" + lines(event).map(escapeIcsText).join("\\n"),
        "END:VEVENT",
        "END:VCALENDAR",
    ].filter(Boolean);

    return content.map(foldLine).join("\r\n") + "\r\n";
}

/**
 * Hand an .ics file to the browser so it can be opened in Apple Calendar,
 * Outlook, etc.
 *
 * Browsers block top level navigation to "data:" URLs, so the old
 * window.open("data:text/calendar,...") approach was being dropped silently.
 * A blob download is the only route that still works everywhere.
 *
 * @param {object} event
 * @returns {void}
 */
export function downloadIcs(event) {
    const blob = new Blob([buildIcs(event)], { type: "text/calendar;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    if (typeof link.download === "undefined") {
        window.location.href = url;
        return;
    }

    link.href = url;
    link.download = event.filename || "event.ics";
    link.rel = "noopener";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}
